import { css } from '@emotion/react';
import type { Invitation } from '@zapier/authentication';
import { mediaQueries, useSize } from '@zapier/identity-utils';
import { Avatar, Button, Colors, Typography } from '@zapier/design-system';
import { useTranslations } from 'next-intl';

type Props = {
  invitation: Invitation;
  handleDeclineButtonClick: () => void;
};

const sectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const headerStyles = css`
  ${Typography.SectionHeader}
  ${Typography.PageHeader}
  color: ${Colors.GrayWarm10};
  text-align: center;

  ${mediaQueries.tablet} {
    text-align: left;
  }

  ${mediaQueries.desktop} {
    ${Typography.PageHeader}
  }
`;

const paragraphStyles = css`
  ${mediaQueries.tablet} {
    ${Typography.Body1}
    color: ${Colors.GrayWarm9};
  }
  ${mediaQueries.desktop} {
    ${Typography.Body1}
    color: ${Colors.GrayWarm9};
    line-height: 1.5rem;
  }
`;

function Bold(chunks: React.ReactNode) {
  return <b>{chunks}</b>;
}

export function InvitationPromo({
  invitation,
  handleDeclineButtonClick,
}: Props) {
  const size = useSize();
  const t = useTranslations('invitation-promo');

  const headerText = invitation.account.name
    ? t('header-with-account', { accountName: invitation.account.name })
    : t('header-default', {
        creatorName: invitation.createdBy.full_name,
      });

  const accountName = invitation?.account.name || t('their-account');

  return (
    <section data-test-id="invitation-promo" css={sectionStyles}>
      {/* @ts-ignore size 80 is not valid but its in the design */}
      <Avatar size="60" url={invitation?.account?.logoUrl} />
      <h1 css={headerStyles}>{headerText}</h1>
      <p css={paragraphStyles}>
        {t.rich('description', {
          b: Bold,
          creatorName: invitation?.createdBy.full_name,
          creatorEmail: invitation?.createdBy.email,
          accountName,
        })}
      </p>
      <span>
        <Button
          variant="secondary"
          onPress={handleDeclineButtonClick}
          isFullWidth={size === 'small'}
        >
          {t('decline-button')}
        </Button>
      </span>
    </section>
  );
}
